import { AxiosError } from 'axios';
import { ApiService } from '../../api/api-service';

const SERVICE_ROOT = '/paintdata';

// Type definitions matching backend DTOs
export interface PaintDataInputsDto {
  paintPricePerGallon: number;
  surfaceAreaPerGallon: number;
  surfaceAreaPerLinearFoot: number;
  painterLaborRate: number;
  numberOfPainters: number;
  utilitiesCost: number;
  packagingBandingCost: number;
  avgLinearFtPainted: number;
  workHoursPerMonth: number;
  markupPercentPaint: number;
}

export interface UtilityCostDto {
  utilityType: string;
  monthlyCost: number;
}

export interface PackagingCostDto {
  packagingType: string;
  monthlyCost: number;
}

export interface PaintDataCalculationResultDto {
  averageLinearFootPerHour: number;
  totalSquareInchesPerHour: number;
  totalGallonsPerHour: number;
  paintCostPerHour: number;
  laborCostPerHour: number;
  utilitiesCostPerHour: number;
  packagingCostPerHour: number;
  subtotalPerHour: number;
  totalCostPerHour: number;
  totalCostPerLinearFoot: number;
  totalCostPerSquareInch: number;
  paintCostPerLinearFoot: number;
}

export interface PaintDataVersionResponseDto {
  id: number;
  manufacturerId: number;
  manufacturerName: string;
  versionNumber: number;
  notes?: string;
  created: string;
  createdByUserId: number;
  createdByUserName: string;
  inputs: PaintDataInputsDto;
  calculationResults: PaintDataCalculationResultDto;
  utilityCosts: UtilityCostDto[];
  packagingCosts: PackagingCostDto[];
}

export interface PaintDataVersionRequestDto {
  manufacturerId: number;
  notes: string;
  paintInputs: PaintDataInputsDto;
  utilityCosts: UtilityCostDto[];
  packagingCosts: PackagingCostDto[];
}

export interface LatestVersionInfoDto {
  version: number;
  createdDate: string;
}

/**
 * Service for paint data-related API operations
 */
export class PaintDataService extends ApiService {
  /**
   * Get paint data version by ID
   */
  async getPaintDataVersionById(id: number): Promise<PaintDataVersionResponseDto> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/version/${id}`),
      `Error fetching paint data version ${id}`
    );
  }

  /**
   * Get latest paint data version for a manufacturer
   */
  async getLatestPaintDataVersionByManufacturerId(
    manufacturerId: number
  ): Promise<PaintDataVersionResponseDto> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/manufacturer/${manufacturerId}/latest`),
      `Error fetching latest paint data version for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Get all paint data versions for a manufacturer
   */
  async getPaintDataVersionsByManufacturerId(
    manufacturerId: number
  ): Promise<PaintDataVersionResponseDto[]> {
    return this.handleApiCall(
      () => this.client.get(`${SERVICE_ROOT}/manufacturer/${manufacturerId}/versions`),
      `Error fetching paint data versions for manufacturer ${manufacturerId}`
    );
  }

  /**
   * Create a new paint data version
   */
  async createPaintDataVersion(
    versionRequest: PaintDataVersionRequestDto
  ): Promise<PaintDataVersionResponseDto> {
    return this.handleApiCall(
      () => this.client.post(`${SERVICE_ROOT}/version`, versionRequest),
      'Error creating paint data version'
    );
  }

  /**
   * Get latest version info for a manufacturer
   * Returns null if no versions exist (404)
   */
  async getLatestVersionInfo(manufacturerId: number): Promise<LatestVersionInfoDto | null> {
    try {
      return await this.client.get(`${SERVICE_ROOT}/manufacturer/${manufacturerId}/version-info`);
    } catch (error: unknown) {
      // If 404, it means no versions exist yet - return null instead of throwing
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      // For other errors, re-throw to use standard error handling
      throw error;
    }
  }
}
